import { useState, useEffect } from "react";
import { X, Server, Link2, Trash2, Users, RadioTower, Network, TerminalSquare } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { OltProvision } from "@/components/OltProvision";

const KIND_META = {
  pon: { label: "Porty PON", icon: RadioTower, color: "text-blue-400" },
  sfp: { label: "Porty SFP / SFP+", icon: Network, color: "text-cyan-400" },
  eth: { label: "Porty Ethernet (RJ45)", icon: Network, color: "text-emerald-400" },
};

export default function OLTView({ olt, role, onClose, onRefresh, onOpenClient }) {
  const canEdit = role !== "odczyt";
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null); // {kind, no, key}
  const [af, setAf] = useState({ target_label: "", target_port: "", note: "" });
  const [prov, setProv] = useState(false);

  const reload = () => api.get(`/olt/${olt.id}/ports`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [olt.id]);

  const attach = async () => {
    if (!af.target_label) { toast.error("Podaj nazwę urządzenia/celu"); return; }
    try {
      await api.post(`/olt/${olt.id}/port-attach`, { key: sel.key, target_label: af.target_label, target_port: af.target_port ? Number(af.target_port) : null, note: af.note });
      toast.success("Podłączono do portu"); setAf({ target_label: "", target_port: "", note: "" }); reload(); onRefresh && onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Błąd"); }
  };
  const clearPort = async () => {
    try { await api.post(`/olt/${olt.id}/port-clear`, { key: sel.key }); toast.success("Wyczyszczono port"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Błąd"); }
  };

  const renderSection = (kind) => {
    const arr = data[kind] || [];
    if (!arr.length) return null;
    const M = KIND_META[kind]; const Icon = M.icon;
    return (
      <div className="mb-4" data-testid={`olt-sec-${kind}`}>
        <div className={`text-sm font-heading font-bold mb-2 flex items-center gap-2 ${M.color}`}><Icon size={15} /> {M.label} ({arr.length})</div>
        <div className="grid grid-cols-4 sm:grid-cols-6 gap-1.5">
          {arr.map((p) => {
            const active = sel && sel.key === p.key;
            const occ = kind === "pon" ? (p.clients || []).length > 0 || p.attach : !!p.attach;
            return (
              <button key={p.key} onClick={() => setSel({ kind, no: p.no, key: p.key })} data-testid={`olt-port-${p.key}`}
                className={`rounded-md border px-1.5 py-1.5 text-left transition-colors ${active ? "ring-2 ring-cyan-400" : ""} ${occ ? "bg-red-900/25 border-red-700/50 hover:bg-red-900/40" : "bg-green-900/15 border-green-700/40 hover:bg-green-900/30"}`}>
                <div className="text-[11px] font-mono text-slate-300">{kind === "pon" ? "PON " : kind === "sfp" ? "SFP " : "RJ45 "}{p.no}</div>
                {kind === "pon" && (p.clients || []).length > 0 && <div className="text-[9px] text-red-300 mt-0.5">{p.clients.length} klient(ów)</div>}
                {p.attach && <div className="text-[9px] text-blue-300 truncate mt-0.5">→ {p.attach.target_label}</div>}
                {!occ && <div className="text-[9px] text-green-400 mt-0.5">wolny</div>}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const selData = sel && (data[sel.kind] || []).find((p) => p.key === sel.key);

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/70 p-4" data-testid="olt-view">
      <div className="bg-slate-800 border border-slate-700 rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-700">
          <h3 className="font-heading font-bold text-lg flex items-center gap-2"><Server size={18} className="text-blue-400" /> OLT {olt.name}</h3>
          <div className="flex items-center gap-2">
            {canEdit && <button onClick={() => setProv(true)} data-testid="olt-provision-open" className="text-xs bg-cyan-600 hover:bg-cyan-500 text-white rounded px-2.5 py-1.5 flex items-center gap-1.5"><TerminalSquare size={14} /> Provisioning ONU (CLI)</button>}
            <button onClick={onClose} data-testid="olt-close" className="text-slate-400 hover:text-white"><X size={20} /></button>
          </div>
        </div>
        {!data ? <div className="p-6 text-slate-500 text-sm">Ładowanie…</div> : (
          <div className="overflow-y-auto p-5">
            <div className="text-xs text-slate-400 mb-3">{olt.producent || ""} {olt.model || ""} {olt.ip_address ? `· ${olt.ip_address}` : ""}</div>
            {renderSection("pon")}
            {renderSection("sfp")}
            {renderSection("eth")}
            {(!data.pon.length && !data.sfp.length && !data.eth.length) && (
              <div className="text-sm text-slate-500">Brak zdefiniowanych portów. Uzupełnij w karcie OLT: „Porty PON (razem)", „Porty SFP/SFP+", „Porty Ethernet".</div>
            )}
            {data.pon.length > 0 && !data.has_card && (
              <div className="text-[11px] text-amber-300 bg-amber-950/20 border border-amber-800/40 rounded px-2 py-1.5 mb-3" data-testid="olt-nocard">Porty PON pokażą przypisanych klientów po dodaniu wkładki OLT (moduł „Wkładki OLT").</div>
            )}

            {sel && (
              <div className="rounded-lg border border-slate-700 bg-slate-900/50 p-4 mt-3" data-testid="olt-port-editor">
                <div className="text-sm font-heading font-bold text-cyan-300 mb-2">{KIND_META[sel.kind].label.replace("Porty ", "")} {sel.no}</div>
                {sel.kind === "pon" && (selData?.clients || []).length > 0 && (
                  <div className="mb-3">
                    <div className="text-[11px] uppercase text-slate-500 mb-1 flex items-center gap-1"><Users size={12} /> Podłączeni klienci</div>
                    <div className="flex flex-wrap gap-1.5">
                      {selData.clients.map((c) => (
                        <button key={c.id} onClick={() => onOpenClient && onOpenClient(c.id)} data-testid={`olt-client-${c.id}`}
                          className="text-xs bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded px-2 py-1">{c.name}{c.onu_status ? ` · ${c.onu_status}` : ""}</button>
                      ))}
                    </div>
                  </div>
                )}
                {canEdit && (
                  <div>
                    <div className="text-[11px] uppercase text-slate-500 mb-1 flex items-center gap-1"><Link2 size={12} /> Podłącz urządzenie do portu</div>
                    <div className="grid grid-cols-2 gap-2">
                      <input value={af.target_label} onChange={(e) => setAf((f) => ({ ...f, target_label: e.target.value }))} placeholder="Nazwa (np. Switch A, uplink)" data-testid="olt-attach-label" className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm" />
                      <input type="number" min="1" value={af.target_port} onChange={(e) => setAf((f) => ({ ...f, target_port: e.target.value }))} placeholder="Port docelowy (opcj.)" className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm" />
                    </div>
                    <button onClick={attach} data-testid="olt-attach-apply" className="mt-2 bg-blue-600 hover:bg-blue-500 text-white rounded px-3 py-1.5 text-sm flex items-center gap-1.5"><Link2 size={14} /> Podłącz</button>
                    {selData?.attach && <button onClick={clearPort} data-testid="olt-clear-port" className="mt-2 ml-3 text-xs text-red-400 hover:text-red-300 inline-flex items-center gap-1"><Trash2 size={12} /> Wyczyść port</button>}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      {prov && <OltProvision defaultPort={sel?.no ? String(sel.no) : ""} onClose={() => setProv(false)} />}
    </div>
  );
}
